import { redirect } from "react-router-dom";
import { getPreferredMerchantId, setActiveMerchantId } from "@/lib/api";
import { eden } from "@/lib/eden";
import { edenData } from "@/lib/eden-data";
import { auth, authReady, isPasswordUserUnverified } from "@/lib/firebase";
import { getRedirectTo, withRedirectTo } from "@/lib/redirect-to";
import { DEFAULT_AUTHENTICATED_HOME } from "@/lib/routes";

export interface Session {
  uid: string;
  email: string | null;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  onboardedAt: string | null;
  role: string | null;
  merchantId: string | null;
  memberships: Array<{ merchantId: string; role: string | null }>;
}

interface MePayload {
  idpUid: string;
  email: string | null;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  onboardedAt: string | Date | null;
  role: string | null;
  merchantId: string | null;
  activeMerchantId?: string | null;
  memberships?: Array<{ merchantId: string; role?: string | null }>;
}

function pickMerchantId(data: MePayload): string | null {
  const ids = new Set((data.memberships ?? []).map((m) => m.merchantId));
  const preferred = getPreferredMerchantId();
  if (preferred && ids.has(preferred)) return preferred;
  if (data.merchantId && ids.has(data.merchantId)) return data.merchantId;
  if (data.activeMerchantId && ids.has(data.activeMerchantId)) return data.activeMerchantId;
  return data.memberships?.[0]?.merchantId ?? null;
}

function toSession(me: MePayload, activeMerchantId: string | null): Session {
  const onboardedAt = me.onboardedAt instanceof Date ? me.onboardedAt.toISOString() : (me.onboardedAt ?? null);
  const memberships = (me.memberships ?? []).map((m) => ({
    merchantId: m.merchantId,
    role: m.role ?? null,
  }));
  // `me.role` is the backend's header-resolved role, which is null on the
  // first /auth/me after login (the X-Merchant-Id header isn't set until we
  // call setActiveMerchantId below). Derive role from the merchant we actually
  // pick as active — that's the one every subsequent write (incl. setName)
  // targets — so an invited admin never looks like an owner here.
  const activeRole = memberships.find((m) => m.merchantId === activeMerchantId)?.role ?? me.role ?? null;
  return {
    uid: me.idpUid,
    email: me.email,
    displayName: me.displayName,
    firstName: me.firstName,
    lastName: me.lastName,
    phone: me.phone,
    onboardedAt,
    role: activeRole,
    merchantId: activeMerchantId,
    memberships,
  };
}

let cached: { uid: string; promise: Promise<Session | null> } | null = null;

export function resetSessionCache(): void {
  cached = null;
}

export async function loadSession(): Promise<Session | null> {
  await authReady;
  const user = auth.currentUser;
  if (!user) {
    cached = null;
    return null;
  }
  // Unverified password accounts get a 403 EMAIL_NOT_VERIFIED from /auth/me.
  // Treat them as "no usable session" so callers never hit that error; the
  // loaders below route them to /verify-email via `verifyGate()`.
  if (isPasswordUserUnverified(user)) {
    cached = null;
    return null;
  }
  if (cached && cached.uid === user.uid) return cached.promise;
  const promise = (async () => {
    const me = edenData(await eden.v1.auth.me.get());
    const activeMerchantId = pickMerchantId(me);
    setActiveMerchantId(activeMerchantId);
    return toSession(me, activeMerchantId);
  })();
  cached = { uid: user.uid, promise };
  return promise;
}

function loginRedirect(request: Request): Response {
  const url = new URL(request.url);
  return redirect(withRedirectTo("/login", url.pathname + url.search));
}

/**
 * Bounce unverified password accounts to /verify-email before any authed
 * loader touches the API. Google logins and verified password users pass
 * through. Throws a redirect (consumed by the route's errorElement).
 */
async function verifyGate(): Promise<void> {
  await authReady;
  if (auth.currentUser && isPasswordUserUnverified(auth.currentUser)) {
    throw redirect("/verify-email");
  }
}

function redirectToParam(request: Request): string | null {
  // `redirect_to` is base64-encoded on the wire; decode + validate it back to a
  // safe internal path (or null) via the shared helper.
  return getRedirectTo(new URL(request.url).searchParams);
}

export async function requireAuthLoader({ request }: { request: Request }): Promise<Session> {
  await verifyGate();
  const session = await loadSession();
  if (!session) throw loginRedirect(request);
  return session;
}

async function consentGate(): Promise<void> {
  try {
    const pending = edenData(await eden.v1.consents.pending.get()) ?? [];
    if (Array.isArray(pending) && pending.length > 0) throw redirect("/consent");
  } catch (err) {
    if (err instanceof Response) throw err; // the redirect above
    // fail-open: a flaky consents endpoint must not lock users out.
  }
}

export async function requireOnboardedLoader({ request }: { request: Request }): Promise<Session> {
  await verifyGate();
  const session = await loadSession();
  if (!session) throw loginRedirect(request);
  if (!session.onboardedAt) throw redirect("/register/profile");
  await consentGate();
  return session;
}

export async function profileGateLoader({ request }: { request: Request }): Promise<{
  firstNamePrefill: string;
  lastNamePrefill: string;
  isJoin: boolean;
  redirectTo: string | null;
}> {
  await verifyGate();
  const session = await loadSession();
  if (!session) throw loginRedirect(request);
  const redirectTo = redirectToParam(request);
  if (session.onboardedAt) {
    // `redirectTo` is already a validated safe internal path (or null).
    throw redirect(redirectTo ?? DEFAULT_AUTHENTICATED_HOME);
  }
  const parts = (session.displayName ?? "").trim().split(/\s+/);
  const firstNamePrefill = parts[0] ?? "";
  const lastNamePrefill = parts.slice(1).join(" ");
  // "Joined" = the user already belongs to a merchant — an invited member who
  // accepted (membership created at /invite), or an owner re-entering
  // onboarding after already creating their shop. Such users skip the shop-name
  // step. A brand-new signup has ZERO memberships and creates their own
  // merchant on submit, so they ARE asked for a shop name. Membership (not a
  // pending invite by email, nor the redirect URL) is the single source of
  // truth — a user with a pending invite who signed up WITHOUT the link has no
  // membership and becomes a normal new owner.
  const isJoin = session.memberships.length > 0;
  return { firstNamePrefill, lastNamePrefill, isJoin, redirectTo };
}

export async function publicOnlyLoader(): Promise<null> {
  await authReady;
  if (!auth.currentUser) return null;
  // A registered-but-unverified password user must finish verification rather
  // than sit on /login or /register.
  if (isPasswordUserUnverified(auth.currentUser)) throw redirect("/verify-email");
  const session = await loadSession();
  if (!session) return null;
  throw redirect(session.onboardedAt ? DEFAULT_AUTHENTICATED_HOME : "/register/profile");
}

/**
 * Loader for /verify-email. Only an unverified password user belongs here —
 * signed-out users go to /login, already-verified (or Google) users go home.
 */
export async function verifyEmailLoader(): Promise<null> {
  await authReady;
  const user = auth.currentUser;
  if (!user) throw redirect("/login");
  if (!isPasswordUserUnverified(user)) throw redirect(DEFAULT_AUTHENTICATED_HOME);
  return null;
}
